<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">
        <!-- Toggle Filter Button -->
        <button @click="toggleFilter"
            class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
            <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
        </button>

        <!-- Sliding Sidebar Filter -->
        <transition name="slide">
            <aside v-show="showFilter"
                class="fixed top-0 left-0 h-full w-72 bg-white shadow-lg z-40 flex flex-col border-r">
                <div class="p-6 border-b">
                    <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
                        <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
                        Filters
                    </h2>
                </div>
                <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Academic Year</label>
                        <select v-model="filters.academic_year"
                            class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/60">
                            <option value="">All</option>
                            <option v-for="year in academicYears" :key="year" :value="year">
                                {{ year }}
                            </option>
                        </select>
                    </div>

                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Entity</label>
                        <select v-model="filters.entity"
                            class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/60">
                            <option value="">All</option>
                            <option v-for="entity in entities" :key="entity.value" :value="entity.value">
                                {{ entity.label }}
                            </option>
                        </select>
                    </div>

                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Program</label>
                        <select v-model="filters.program"
                            class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/60">
                            <option value="">All</option>
                            <option v-for="program in availablePrograms" :key="program" :value="program">
                                {{ program }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="p-6 flex justify-center space-x-6 border-t">
                    <button @click.stop="clearFilters"
                        class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold rounded-md">
                        Clear
                    </button>
                    <button @click.stop="applyFilters"
                        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold rounded-md">
                        Apply
                    </button>
                </div>
            </aside>
        </transition>

        <!-- Main Content -->
        <div class="container mx-auto px-4 sm:px-8 py-8 space-y-8">
            <!-- Breadcrumb -->
            <nav class="text-sm text-gray-600 mb-6 flex">
                <router-link to="/dashboard" class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                <span class="mx-1">/</span>
                <router-link to="/fchs" class="hover:text-blue-700 transition font-medium">FCHS</router-link>
                <span class="mx-1">/</span>
                <router-link to="/international-recognition"
                    class="hover:text-blue-700 transition font-medium">International Recognition</router-link>
                <span class="mx-1">/</span>
                <span class="text-blue-900 font-semibold">International Accreditation</span>
            </nav>

            <!-- Header -->
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                    <h1 class="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                        International Accreditation
                    </h1>
                    <p class="text-gray-600">
                        Overview of international accreditations across entities and programs
                    </p>
                </div>
                <button @click="openCreateModal"
                    class="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                    </svg>
                    <span>New Accreditation</span>
                </button>
            </div>

            <!-- Summary Tiles -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div class="bg-white rounded-xl shadow-md p-6 border-l-4 border-primary flex flex-col">
                    <div class="flex items-center justify-between mb-3">
                        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                            Total Accreditations
                        </h3>
                        <font-awesome-icon :icon="['fas', 'globe']" class="text-primary text-xl" />
                    </div>
                    <div class="text-3xl font-bold text-gray-900">
                        {{ total || 0 }}
                    </div>
                    <p class="mt-2 text-xs text-gray-500">
                        Across all entities and academic years
                    </p>
                </div>

                <div class="bg-white rounded-xl shadow-md p-6 border-l-4 border-emerald-500 flex flex-col">
                    <div class="flex items-center justify-between mb-3">
                        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                            By Academic Year
                        </h3>
                        <font-awesome-icon :icon="['fas', 'calendar-alt']" class="text-emerald-500 text-xl" />
                    </div>
                    <div class="space-y-1 max-h-20 overflow-y-auto text-sm text-gray-800">
                        <div v-for="yearStat in totalByAcademicYear" :key="yearStat.academic_year"
                            class="flex justify-between">
                            <span>{{ yearStat.academic_year }}</span>
                            <span class="font-semibold">{{ yearStat.count }}</span>
                        </div>
                        <p v-if="!totalByAcademicYear.length" class="text-gray-500 text-sm">
                            No data yet
                        </p>
                    </div>
                </div>

                <div class="bg-white rounded-xl shadow-md p-6 border-l-4 border-indigo-500 flex flex-col">
                    <div class="flex items-center justify-between mb-3">
                        <h3 class="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                            By Entity
                        </h3>
                        <font-awesome-icon :icon="['fas', 'university']" class="text-indigo-500 text-xl" />
                    </div>
                    <div class="space-y-1 max-h-20 overflow-y-auto text-sm text-gray-800">
                        <div v-for="entityStat in totalByEntity" :key="entityStat.entity" class="flex justify-between">
                            <span>{{ displayEntity(entityStat.entity) }}</span>
                            <span class="font-semibold">{{ entityStat.count }}</span>
                        </div>
                        <p v-if="!totalByEntity.length" class="text-gray-500 text-sm">
                            No data yet
                        </p>
                    </div>
                </div>
            </div>

            <!-- Filters Chips -->
            <div class="flex flex-wrap gap-3 items-center">
                <span class="text-sm font-medium text-gray-700">Active Filters:</span>
                <div v-if="!hasActiveFilters"
                    class="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-xs text-gray-500">
                    None
                </div>
                <div v-if="filters.academic_year"
                    class="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-xs text-blue-700">
                    <span>Academic Year: {{ filters.academic_year }}</span>
                    <button class="ml-2 text-blue-500 hover:text-blue-700"
                        @click="filters.academic_year = ''; refetchList(); loadStatistics();">
                        ×
                    </button>
                </div>
                <div v-if="filters.entity"
                    class="inline-flex items-center px-3 py-1 rounded-full bg-green-50 text-xs text-green-700">
                    <span>Entity: {{ displayEntity(filters.entity) }}</span>
                    <button class="ml-2 text-green-500 hover:text-green-700"
                        @click="filters.entity = ''; refetchList(); loadStatistics();">
                        ×
                    </button>
                </div>
                <div v-if="filters.program"
                    class="inline-flex items-center px-3 py-1 rounded-full bg-purple-50 text-xs text-purple-700">
                    <span>Program: {{ filters.program }}</span>
                    <button class="ml-2 text-purple-500 hover:text-purple-700"
                        @click="filters.program = ''; refetchList(); loadStatistics();">
                        ×
                    </button>
                </div>
            </div>

            <!-- List -->
            <div class="bg-white rounded-xl shadow-md overflow-hidden">
                <div class="px-4 py-3 border-b flex items-center justify-between">
                    <h2 class="text-lg font-semibold text-gray-800">
                        Accreditations
                    </h2>
                    <div class="flex items-center gap-3 text-sm text-gray-500">
                        <span v-if="paginatorInfo">
                            Page {{ paginatorInfo.currentPage }} of {{ paginatorInfo.lastPage }}
                        </span>
                    </div>
                </div>

                <!-- Loading -->
                <div v-if="isLoading" class="p-6 space-y-3">
                    <div v-for="i in 3" :key="i" class="animate-pulse h-16 bg-gray-100 rounded-md"></div>
                </div>

                <!-- Table -->
                <div v-else>
                    <div v-if="items && items.length" class="overflow-x-auto">
                        <table class="min-w-full divide-y divide-gray-200 text-sm">
                            <thead class="bg-gray-50">
                                <tr>
                                    <th
                                        class="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        #
                                    </th>
                                    <th
                                        class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Program
                                    </th>
                                    <th
                                        class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Title
                                    </th>
                                    <th
                                        class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Entity
                                    </th>
                                    <th
                                        class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Academic Year
                                    </th>
                                    <th
                                        class="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Created By
                                    </th>
                                    <th
                                        class="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="bg-white divide-y divide-gray-100">
                                <tr v-for="(item, index) in items" :key="item.id" class="hover:bg-gray-50 transition">
                                    <td class="px-4 py-3 text-center text-gray-700 font-medium">
                                        {{ (page - 1) * first + index + 1 }}
                                    </td>
                                    <td class="px-4 py-3 text-gray-900 font-medium">
                                        {{ item.program }}
                                    </td>
                                    <td class="px-4 py-3 text-gray-700 max-w-md">
                                        <div class="font-semibold text-gray-900">
                                            {{ item.title }}
                                        </div>
                                    </td>
                                    <td class="px-4 py-3 text-gray-700">
                                        {{ displayEntity(item.entity) }}
                                    </td>
                                    <td class="px-4 py-3 text-gray-700">
                                        {{ item.academic_year }}
                                    </td>
                                    <td class="px-4 py-3 text-gray-700">
                                        <span v-if="item.created_by">
                                            <a @click.prevent="goToProfile(item.created_by.token)"
                                                class="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors">
                                                {{ item.created_by.name }}
                                            </a>
                                        </span>
                                        <span v-else class="text-gray-400 italic">
                                            System
                                        </span>
                                    </td>
                                    <td class="px-4 py-3 text-right">
                                        <div class="inline-flex items-center gap-2">
                                            <button v-if="canEdit(item)" @click="openEditModal(item)"
                                                class="px-3 py-1 text-xs border border-blue-500 text-blue-600 rounded-full hover:bg-blue-50">
                                                Edit
                                            </button>
                                            <button v-if="canDelete(item)" @click="confirmDelete(item)"
                                                class="px-3 py-1 text-xs border border-red-500 text-red-600 rounded-full hover:bg-red-50">
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-else class="p-10 text-center text-gray-500">
                        No accreditations found for the selected filters.
                    </div>
                </div>

                <!-- Pagination Controls -->
                <div v-if="paginatorInfo && paginatorInfo.total > 0"
                    class="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
                    <div class="text-xs text-gray-500">
                        Showing
                        <span class="font-semibold">{{ paginatorInfo.firstItem || 0 }}</span>
                        to
                        <span class="font-semibold">{{ paginatorInfo.lastItem || 0 }}</span>
                        of
                        <span class="font-semibold">{{ paginatorInfo.total }}</span>
                        entries
                    </div>
                    <div class="flex items-center gap-2">
                        <button :disabled="page <= 1" @click="changePage(page - 1)"
                            class="px-3 py-1 text-xs rounded-md border bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                            Previous
                        </button>
                        <span class="text-xs text-gray-600">
                            Page {{ page }} of {{ paginatorInfo.lastPage }}
                        </span>
                        <button :disabled="!paginatorInfo.hasMorePages" @click="changePage(page + 1)"
                            class="px-3 py-1 text-xs rounded-md border bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                            Next
                        </button>
                    </div>
                </div>
            </div>

            <!-- Create / Edit Modal -->
            <transition name="fade">
                <div v-if="showFormModal"
                    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4">
                        <div class="px-6 py-4 border-b flex items-center justify-between">
                            <h3 class="text-lg font-semibold text-gray-800">
                                {{ isEditing ? 'Edit Accreditation' : 'New Accreditation' }}
                            </h3>
                            <button class="text-gray-400 hover:text-gray-600" @click="closeFormModal">
                                ×
                            </button>
                        </div>

                        <form @submit.prevent="submitForm" class="px-6 py-5 space-y-4">
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">
                                    Entity
                                    <span class="text-red-500">*</span>
                                </label>
                                <select v-model="form.entity"
                                    class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
                                    required>
                                    <option value="">Select entity</option>
                                    <option v-for="entity in entities" :key="entity.value" :value="entity.value">
                                        {{ entity.label }}
                                    </option>
                                </select>
                            </div>

                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">
                                    Program
                                    <span class="text-red-500">*</span>
                                </label>
                                <select v-model="form.program"
                                    class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
                                    required>
                                    <option value="">Select program</option>
                                    <option v-for="program in formPrograms" :key="program" :value="program">
                                        {{ program }}
                                    </option>
                                </select>
                            </div>

                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">
                                    Academic Year
                                    <span class="text-red-500">*</span>
                                </label>
                                <input v-model="form.academic_year" type="text" placeholder="e.g., 2025-2026"
                                    class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/60"
                                    required />
                            </div>

                            <div>
                                <label class="block text-sm font-semibold text-gray-700 mb-1">
                                    Title
                                    <span class="text-red-500">*</span>
                                </label>
                                <textarea v-model="form.title" rows="3" placeholder="Accreditation title"
                                    class="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/60 resize-none"
                                    required></textarea>
                            </div>

                            <p v-if="formError" class="text-sm text-red-600">
                                {{ formError }}
                            </p>

                            <div class="pt-3 flex items-center justify-end gap-3">
                                <button type="button"
                                    class="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                                    @click="closeFormModal">
                                    Cancel
                                </button>
                                <button type="submit" :disabled="submitting"
                                    class="px-5 py-2 text-sm rounded-md bg-primary text-white hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2">
                                    <span v-if="submitting">Saving...</span>
                                    <span v-else>{{ isEditing ? 'Update' : 'Create' }}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';

const INTERNATIONAL_ACCREDITATIONS_QUERY = gql`
  query InternationalAccreditations(
    $first: Int!
    $page: Int!
    $academic_year: String
    $entity: String
    $program: String
  ) {
    internationalAccreditations(
      first: $first
      page: $page
      academic_year: $academic_year
      entity: $entity
      program: $program
    ) {
      error
      error_code
      message
      data {
        id
        token
        title
        entity
        program
        academic_year
        created_by {
          id
          token
          name
        }
        created_at
        updated_at
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

const GET_INTERNATIONAL_ACCREDITATION_STATISTICS = gql`
  query GetInternationalAccreditationStatistics(
    $academic_year: String
    $entity: String
    $program: String
  ) {
    internationalAccreditationStatistics(
      academic_year: $academic_year
      entity: $entity
      program: $program
    ) {
      error
      error_code
      message
      data {
        total
        totalByAcademicYear {
          academic_year
          count
        }
        totalByEntity {
          entity
          count
        }
      }
    }
  }
`;

const CREATE_INTERNATIONAL_ACCREDITATION = gql`
  mutation CreateInternationalAccreditation($input: InternationalAccreditationInput!) {
    createInternationalAccreditation(input: $input) {
      error
      error_code
      message
      data {
        id
      }
    }
  }
`;

const UPDATE_INTERNATIONAL_ACCREDITATION = gql`
  mutation UpdateInternationalAccreditation($id: ID!, $input: InternationalAccreditationInput!) {
    updateInternationalAccreditation(id: $id, input: $input) {
      error
      error_code
      message
      data {
        id
      }
    }
  }
`;

const DELETE_INTERNATIONAL_ACCREDITATION = gql`
  mutation DeleteInternationalAccreditation($id: ID!) {
    deleteInternationalAccreditation(id: $id) {
      error
      error_code
      message
    }
  }
`;

const GET_ACADEMIC_YEARS_QUERY = gql`
  query GetInternationalAccreditationAcademicYears {
    internationalAccreditationAcademicYears
  }
`;

export default {
    name: 'international-accreditation',
    data() {
        return {
            showFilter: false,
            first: 10,
            page: 1,
            filters: {
                academic_year: '',
                entity: '',
                program: '',
            },
            showFormModal: false,
            isEditing: false,
            submitting: false,
            formError: '',
            isLoading: false,
            form: {
                id: null,
                title: '',
                entity: '',
                program: '',
                academic_year: '',
            },
            entities: [
                { value: 'FCHS', label: 'FCHS' },
                { value: 'ADPOLY', label: 'ADPOLY' },
                { value: 'ATS', label: 'ATS' },
                { value: 'ADVETI', label: 'ADVETI' },
            ],
            programsByEntity: {
                FCHS: [
                    'Nursing',
                    'Pharmacy',
                    'Medical Imaging',
                    'Physiotherapy',
                    'Emergency Health',
                    'Midwifery',
                    'Psychology',
                ],
                ADPOLY: [],
                ATS: [],
                ADVETI: [],
            },
            academicYears: [],
            internationalAccreditations: null,
            statistics: null,
        };
    },
    created() {
        this.loadInternationalAccreditations();
        this.loadStatistics();
        this.fetchAcademicYears();
    },
    computed: {
        items() {
            if (!this.internationalAccreditations || this.internationalAccreditations.error) {
                return [];
            }
            return this.internationalAccreditations.data || [];
        },
        paginatorInfo() {
            if (!this.internationalAccreditations || this.internationalAccreditations.error) {
                return null;
            }
            return this.internationalAccreditations.paginatorInfo;
        },
        total() {
            if (!this.statistics || !this.statistics.data) {
                return 0;
            }
            return this.statistics.data.total || 0;
        },
        totalByAcademicYear() {
            if (!this.statistics || !this.statistics.data) {
                return [];
            }
            return this.statistics.data.totalByAcademicYear || [];
        },
        totalByEntity() {
            if (!this.statistics || !this.statistics.data) {
                return [];
            }
            return this.statistics.data.totalByEntity || [];
        },
        availablePrograms() {
            if (!this.filters.entity) {
                // Show all unique programs from the mapping when no entity filter
                const all = []
                    .concat(this.programsByEntity.FCHS || [])
                    .concat(this.programsByEntity.ADPOLY || [])
                    .concat(this.programsByEntity.ATS || [])
                    .concat(this.programsByEntity.ADVETI || []);
                const unique = [];
                for (let i = 0; i < all.length; i += 1) {
                    if (unique.indexOf(all[i]) === -1) {
                        unique.push(all[i]);
                    }
                }
                return unique;
            }
            return this.programsByEntity[this.filters.entity] || [];
        },
        formPrograms() {
            if (!this.form.entity) {
                return [];
            }
            return this.programsByEntity[this.form.entity] || [];
        },
        hasActiveFilters() {
            return !!(this.filters.academic_year || this.filters.entity || this.filters.program);
        },
        role() {
            return this.$store.getters.role;
        },
        userId() {
            return this.$store.getters.userId;
        },
    },
    methods: {
        toggleFilter() {
            this.showFilter = !this.showFilter;
        },
        clearFilters() {
            this.filters = {
                academic_year: '',
                entity: '',
                program: '',
            };
            this.page = 1;
            this.refetchList();
            this.loadStatistics();
            this.showFilter = false;
        },
        applyFilters() {
            this.page = 1;
            this.refetchList();
            this.loadStatistics();
            this.showFilter = false;
        },
        changePage(newPage) {
            if (newPage < 1 || (this.paginatorInfo && newPage > this.paginatorInfo.lastPage)) {
                return;
            }
            this.page = newPage;
            this.refetchList();
        },
        refetchList() {
            this.loadInternationalAccreditations();
        },
        async loadStatistics() {
            try {
                const response = await this.$apollo.query({
                    query: GET_INTERNATIONAL_ACCREDITATION_STATISTICS,
                    variables: {
                        academic_year: this.filters.academic_year || null,
                        entity: this.filters.entity || null,
                        program: this.filters.program || null,
                    },
                    fetchPolicy: 'network-only',
                });

                if (response.data && response.data.internationalAccreditationStatistics) {
                    this.statistics = response.data.internationalAccreditationStatistics;
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to load statistics', error);
            }
        },
        async fetchAcademicYears() {
            try {
                const response = await this.$apollo.query({
                    query: GET_ACADEMIC_YEARS_QUERY,
                    fetchPolicy: 'network-only',
                });

                if (response.data && response.data.internationalAccreditationAcademicYears) {
                    this.academicYears = response.data.internationalAccreditationAcademicYears;
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to fetch academic years', error);
            }
        },
        displayEntity(entity) {
            if (entity === 'ATHS') {
                return 'ATS';
            }
            return entity;
        },
        goToProfile(token) {
            if (token) {
                this.$router.push(`/profile/${token}`);
            }
        },
        canEdit(item) {
            if (this.role === 'superadmin') {
                return true;
            }
            if (!item.created_by) {
                return false;
            }
            return item.created_by.id === this.userId;
        },
        canDelete(item) {
            return this.canEdit(item);
        },
        openCreateModal() {
            this.isEditing = false;
            this.formError = '';
            this.form = {
                id: null,
                title: '',
                entity: '',
                program: '',
                academic_year: this.academicYears[0] || '',
            };
            this.showFormModal = true;
        },
        openEditModal(item) {
            this.isEditing = true;
            this.formError = '';
            this.form = {
                id: item.id,
                title: item.title,
                entity: this.displayEntity(item.entity),
                program: item.program,
                academic_year: item.academic_year,
            };
            this.showFormModal = true;
        },
        closeFormModal() {
            if (this.submitting) {
                return;
            }
            this.showFormModal = false;
        },
        async loadInternationalAccreditations() {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: INTERNATIONAL_ACCREDITATIONS_QUERY,
                    variables: {
                        first: this.first,
                        page: this.page,
                        academic_year: this.filters.academic_year || null,
                        entity: this.filters.entity || null,
                        program: this.filters.program || null,
                    },
                    fetchPolicy: 'network-only',
                });

                this.internationalAccreditations = response.data.internationalAccreditations;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to load international accreditations', error);
            }
            this.isLoading = false;
        },
        async submitForm() {
            this.formError = '';

            if (!this.form.title || !this.form.entity || !this.form.program || !this.form.academic_year) {
                this.formError = 'All fields are mandatory.';
                return;
            }

            this.submitting = true;

            const payload = {
                title: this.form.title,
                entity: this.form.entity,
                program: this.form.program,
                academic_year: this.form.academic_year,
            };

            try {
                if (this.isEditing && this.form.id) {
                    await this.$apollo.mutate({
                        mutation: UPDATE_INTERNATIONAL_ACCREDITATION,
                        variables: {
                            id: this.form.id,
                            input: payload,
                        },
                    });

                    this.$swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: 'Accreditation updated successfully',
                        showConfirmButton: false,
                        timer: 2000,
                        timerProgressBar: true,
                    });
                } else {
                    await this.$apollo.mutate({
                        mutation: CREATE_INTERNATIONAL_ACCREDITATION,
                        variables: {
                            input: payload,
                        },
                    });

                    this.$swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: 'Accreditation created successfully',
                        showConfirmButton: false,
                        timer: 2000,
                        timerProgressBar: true,
                    });
                }

                this.showFormModal = false;
                this.refetchList();
                this.loadStatistics();
                this.fetchAcademicYears();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to save accreditation', error);
                this.formError = 'Failed to save accreditation. Please try again.';
            } finally {
                this.submitting = false;
            }
        },
        async confirmDelete(item) {
            const result = await this.$swal.fire({
                title: 'Are you sure?',
                text: `You are about to delete the accreditation for "${item.program}" - "${item.title}". This action cannot be undone!`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Yes, delete it!',
                cancelButtonText: 'Cancel'
            });

            if (!result.isConfirmed) {
                return;
            }

            try {
                await this.$apollo.mutate({
                    mutation: DELETE_INTERNATIONAL_ACCREDITATION,
                    variables: {
                        id: item.id,
                    },
                });

                this.$swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Accreditation deleted successfully',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });

                this.refetchList();
                this.loadStatistics();
                this.fetchAcademicYears();
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to delete accreditation', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to delete accreditation. Please try again.',
                });
            }
        },
    },
};
</script>

<style scoped lang="scss">
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(-100%);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>